import { computed, inject, nextTick, ref, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import useToast from '@lib/composables/useToast'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import type { AppFile } from '@lib/types/app'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { doesLocalOverrideExist, getLocalOverrideUrl } from '@lib/util/dev.util'
import { toDimensionValue } from '@lib/util/style.util'
import { waitForIframeInitialize } from '@lib/utils/iframeInitialize'

const selectedApp = ref<AppFile | null>(null)
const appIframeRef = ref<HTMLIFrameElement | null>(null)

export function usePopupApps() {
  const { t } = useI18n()
  const { toast } = useToast()
  const { activeCanvas, activeCanvasSections, isViewOnlyMode, isEditMode, isFullscreen } = useCanvas()
  const { activeSectionId } = useCanvasPages()

  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const allPopupApps = inject<ComputedRef<AppFile[]>>(
    'canvasPopupApps',
    computed(() => []),
  )

  const selectedAppUrl = computed(() => {
    if (!selectedApp.value?.name) return ''

    if (doesLocalOverrideExist(selectedApp.value.name)) {
      return getLocalOverrideUrl(selectedApp.value.name) as string
    }

    return selectedApp.value.content_url || ''
  })

  // Filter apps based on mode configuration
  const fullscreenModeApps = computed(() => {
    return allPopupApps.value.filter((app) => {
      const showInFullscreen = app.app_metadata?.app_options?.mode_config?.fullscreen?.show
      return showInFullscreen !== false // default is true
    })
  })

  const presentationModeApps = computed(() => {
    return allPopupApps.value.filter((app) => {
      const showInPresentation = app.app_metadata?.app_options?.mode_config?.presentation?.show
      return showInPresentation !== false // default is true
    })
  })

  const editModeApps = computed(() => {
    return allPopupApps.value.filter((app) => {
      const showInEdit = app.app_metadata?.app_options?.mode_config?.edit?.show
      return showInEdit !== false // default is true
    })
  })

  // Get the appropriate apps list based on current mode
  const popupApps = computed(() => {
    if (isFullscreen.value) {
      return fullscreenModeApps.value
    } else if (isEditMode.value) {
      return editModeApps.value
    } else {
      return presentationModeApps.value
    }
  })

  const shouldShowPopupApps = computed(() => {
    return launchDarkly.value.enable_popup_apps && popupApps.value.length > 0 && !isViewOnlyMode.value
  })

  const hasMultipleApps = computed(() => popupApps.value.length > 1)
  const hasSingleApp = computed(() => popupApps.value.length === 1)
  const singleApp = computed(() => (hasSingleApp.value ? popupApps.value[0] : null))

  const getModalStyle = computed(() => {
    if (!selectedApp.value?.app_metadata?.app_options?.dimensions) return {}

    const { width, height } = selectedApp.value.app_metadata?.app_options?.dimensions || {}
    const placement = selectedApp.value.app_metadata?.app_options?.placement || 'center'

    const style: Record<string, string> = {
      width: toDimensionValue(width, '800px'),
      height: toDimensionValue(height, '600px'),
      maxWidth: '100vw',
      maxHeight: '100vh',
      position: 'fixed',
    }

    const placementConfig: Record<string, { [key: string]: string }> = {
      'top-left': { left: '20px', top: '20px', transform: 'none' },
      'top-center': { left: '50%', top: '20px', transform: 'translateX(-50%)' },
      'top-right': { right: '20px', top: '20px', transform: 'none' },
      left: { left: '20px', top: '50%', transform: 'translateY(-50%)' },
      right: { right: '20px', top: '50%', transform: 'translateY(-50%)' },
      'bottom-left': { left: '20px', bottom: '20px', transform: 'none' },
      'bottom-center': { left: '50%', bottom: '20px', transform: 'translateX(-50%)' },
      'bottom-right': { right: '20px', bottom: '20px', transform: 'none' },
      center: { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' },
    }

    const placementStyles = placementConfig[placement] || placementConfig.center
    Object.assign(style, placementStyles)

    return style
  })

  async function onAppClick(app: AppFile) {
    selectedApp.value = app

    try {
      await nextTick()

      waitForIframeInitialize(
        appIframeRef.value,
        {
          active_canvas: activeCanvas.value,
          active_section: activeSectionId.value ? activeCanvasSections.value?.[activeSectionId.value] : undefined,
        },
        'canvas-popup',
        (result?: any) => {
          console.info(`App ${app.name} callback received:`, result)
          selectedApp.value = null
        },
        { componentName: 'PopupApps' },
      )
    } catch (err) {
      console.error('Error opening app:', err)
      toast.error(t('canvasUI.canvasBuilder.notifications.appLaunch.error'))
    }
  }

  function closeAppModal() {
    selectedApp.value = null
  }

  // Get current mode info for apps (icon, order_override)
  const getCurrentModeConfig = (app: AppFile) => {
    const modeConfig = app.app_metadata?.app_options?.mode_config
    if (isFullscreen.value) {
      return modeConfig?.fullscreen
    } else if (isEditMode.value) {
      return modeConfig?.edit
    } else {
      return modeConfig?.presentation
    }
  }

  // Sort apps by order_override if available
  const sortedPopupApps = computed(() => {
    return [...popupApps.value].sort((a, b) => {
      const aConfig = getCurrentModeConfig(a)
      const bConfig = getCurrentModeConfig(b)
      const aOrder = aConfig?.order_override ?? a.app_metadata?.order_override ?? 0
      const bOrder = bConfig?.order_override ?? b.app_metadata?.order_override ?? 0
      return aOrder - bOrder
    })
  })

  const getAppIcon = (app: AppFile) => {
    const modeConfig = getCurrentModeConfig(app)
    return modeConfig?.icon || app.app_metadata?.icon || 'fa-kit fa-regular-rocket-circle-question'
  }

  return {
    // State
    popupApps: sortedPopupApps,
    selectedApp,
    selectedAppUrl,
    appIframeRef,

    // Mode-specific apps (for debugging/inspection)
    fullscreenModeApps,
    presentationModeApps,
    editModeApps,
    allPopupApps,

    // Computed
    shouldShowPopupApps,
    hasMultipleApps,
    hasSingleApp,
    singleApp,
    getModalStyle,

    // Methods
    onAppClick,
    closeAppModal,
    getCurrentModeConfig,
    getAppIcon,
  }
}
